// FlashcardsPage.jsx
import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, DocumentDuplicateIcon, CheckCircleIcon } from '@heroicons/react/24/outline'; // Added CheckCircleIcon

// Flashcard data
const initialFlashcardsData = [
  { id: 1, front: 'What is the capital of France?', back: 'Paris', mastered: false },
  { id: 2, front: 'What is the chemical symbol for water?', back: 'H2O', mastered: false },
  { id: 3, front: 'Who wrote "Romeo and Juliet"?', back: 'William Shakespeare', mastered: false },
  { id: 4, front: 'What is the largest planet in our solar system?', back: 'Jupiter', mastered: false },
];

// Reusable Flashcard component
const Flashcard = ({ front, back, isFlipped, onFlip, isMastered }) => {
  return (
    <div
      className="relative w-full h-64 cursor-pointer transform-style-3d group perspective-1000" // Added group and perspective
      onClick={onFlip}
    >
      <div
        className={`absolute w-full h-full transition-transform duration-700 ease-in-out transform ${
          isFlipped ? 'rotate-y-180' : ''
        } group-hover:scale-105`} // Added group-hover effect
      >
        {/* Front Side */}
        <div className="absolute w-full h-full bg-white rounded-2xl flex flex-col items-center justify-center text-gray-800 text-2xl font-bold shadow-lg border border-gray-200 backface-hidden p-4">
          {isMastered && (
            <CheckCircleIcon className="w-8 h-8 text-green-500 absolute top-4 right-4" />
          )}
          <span className="text-center">{front}</span>
        </div>

        {/* Back Side */}
        <div className="absolute w-full h-full bg-blue-50 rounded-2xl flex flex-col items-center justify-center text-blue-800 text-2xl font-bold shadow-lg border border-blue-200 rotate-y-180 backface-hidden p-4">
          {isMastered && (
            <CheckCircleIcon className="w-8 h-8 text-green-500 absolute top-4 right-4" />
          )}
          <span className="text-center">{back}</span>
        </div>
      </div>
    </div>
  );
};

// Main Page Component
const FlashcardsPage = () => {
  const [flashcards, setFlashcards] = useState(initialFlashcardsData);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  const handleNext = () => {
    setIsFlipped(false);
    setCurrentIndex((prev) => (prev + 1) % flashcards.length);
  };

  const handlePrev = () => {
    setIsFlipped(false);
    setCurrentIndex((prev) => (prev - 1 + flashcards.length) % flashcards.length);
  };

  const handleMasteredToggle = () => {
    setFlashcards((prevFlashcards) =>
      prevFlashcards.map((card, index) =>
        index === currentIndex ? { ...card, mastered: !card.mastered } : card
      )
    );
  };

  const handleResetMastered = () => {
    setFlashcards(initialFlashcardsData.map(card => ({ ...card, mastered: false })));
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const currentCard = flashcards[currentIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-200 via-pink-200 to-red-200 p-6 flex items-center justify-center font-sans"> {/* Changed gradient and added font-sans */}
      <div className="bg-white rounded-3xl p-8 shadow-2xl border-b-8 border-purple-600 w-full max-w-xl text-center"> {/* Changed border color and shadow */}
        <h1 className="text-4xl font-extrabold text-purple-800 mb-6 flex justify-center items-center"> {/* Changed text color and font weight */}
          <DocumentDuplicateIcon className="w-10 h-10 mr-3 text-purple-600" /> {/* Changed icon color */}
          Flashcards
        </h1>

        <Flashcard
          front={currentCard.front}
          back={currentCard.back}
          isFlipped={isFlipped}
          onFlip={() => setIsFlipped(!isFlipped)}
          isMastered={currentCard.mastered} // Pass mastered status
        />

        <p className="mt-4 text-gray-700"> {/* Changed text color */}
          Card {currentIndex + 1} of {flashcards.length}
          {currentCard.mastered && <span className="ml-2 text-green-600 font-semibold">(Mastered!)</span>}
        </p>

        <div className="flex justify-center items-center space-x-4 mt-6"> {/* Adjusted spacing and alignment */}
          <button
            onClick={handlePrev}
            className="p-3 bg-purple-300 rounded-full shadow-md hover:bg-purple-400 text-purple-800 transition duration-300"
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>
          <button
            onClick={() => setIsFlipped(!isFlipped)}
            className="px-6 py-3 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition duration-300 font-semibold text-lg" // Larger, more prominent flip button
          >
            {isFlipped ? 'Show Front' : 'Flip Card'}
          </button>
          <button
            onClick={handleNext}
            className="p-3 bg-purple-300 rounded-full shadow-md hover:bg-purple-400 text-purple-800 transition duration-300"
          >
            <ChevronRightIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mt-4 flex justify-center space-x-4">
          <button
            onClick={handleMasteredToggle}
            className={`px-4 py-2 rounded-full shadow-md transition duration-300 ${
              currentCard.mastered
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {currentCard.mastered ? 'Unmark Mastered' : 'Mark as Mastered'}
          </button>
          <button
            onClick={handleResetMastered}
            className="px-4 py-2 bg-red-200 text-red-700 rounded-full shadow-md hover:bg-red-300 transition duration-300"
          >
            Reset Mastered
          </button>
        </div>

        <p className="mt-6 text-gray-500 italic text-sm">
          "Repetition is the mother of learning."
        </p>
      </div>
    </div>
  );
};

export default FlashcardsPage;